import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useMemo,
  useEffect,
  ReactNode,
  useRef,
} from 'react';
import { configureStore, Store } from '@reduxjs/toolkit';
import { MapIcon, ModuleConfig } from '@schamburg-web/interfaces';
import { createModuleSlice, ModuleSlice } from '../../util/reduxSlice';
import { DataCacheContext } from '../DataCacheContext/DataCacheContext';
import { useTheme, useMediaQuery } from '@mui/material';

export type ModulesContextValue = {
  modules: ModuleConfig[];
  modulesEnabled: Record<string, boolean>;
  setModules: (val: ModuleConfig[]) => void;
  setModulesEnabled: (module: string, enabled: boolean) => void;
  toggleLayerControl: () => void;
  toggleHarControl: () => void;
  toggleSidebar: () => void;
  layerControlOpen: boolean;
  harOpen: boolean;
  sidebarOpen: boolean;
  // Direct Redux store access for components that need isolation
  getStore: () => Store | null;
  getModuleSlice: (moduleName: string) => ModuleSlice | null;
  updateObject: (
    moduleName: string,
    id: string,
    updates: Partial<MapIcon>
  ) => void;
};

interface State {
  modules: ModuleConfig[];
  modulesEnabled: Record<string, boolean>;
  layerControlOpen: boolean;
  harOpen: boolean;
  sidebarOpen: boolean;
}

const initialState: State = {
  modules: [],
  modulesEnabled: {},
  layerControlOpen: false,
  harOpen: false,
  sidebarOpen: false,
};

type Action =
  | { type: 'SET_Modules'; payload: ModuleConfig[] }
  | { type: 'SET_Enabled'; payload: { module: string; enabled: boolean } }
  | { type: 'TOGGLE_LayerControl' }
  | { type: 'TOGGLE_HarControl' }
  | { type: 'TOGGLE_Sidebar' }
  | { type: 'SET_SidebarOpen'; payload: boolean };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_Modules':
      return {
        ...state,
        modules: action.payload,
        modulesEnabled: action.payload.reduce((acc, module) => {
          acc[module.name] = state.modulesEnabled[module.name] ?? true;
          return acc;
        }, {} as Record<string, boolean>),
      };
    case 'SET_Enabled':
      return {
        ...state,
        modulesEnabled: {
          ...state.modulesEnabled,
          [action.payload.module]: action.payload.enabled,
        },
      };
    case 'TOGGLE_LayerControl':
      return { ...state, layerControlOpen: !state.layerControlOpen };
    case 'TOGGLE_HarControl':
      return { ...state, harOpen: !state.harOpen };
    case 'TOGGLE_Sidebar':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'SET_SidebarOpen':
      return { ...state, sidebarOpen: action.payload };
    default:
      return state;
  }
};

const ModulesContext = createContext<ModulesContextValue | null>(null);

interface ModulesProviderProps {
  children: ReactNode;
}

export const ModulesProvider: React.FC<ModulesProviderProps> = ({
  children,
}) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Get initial sidebar state from sessionStorage or default to false for mobile, true for desktop
  const getInitialSidebarState = () => {
    const stored = sessionStorage.getItem('sidebarOpen');
    if (stored !== null) {
      return stored === 'true';
    }
    // Check window width directly for SSR/initial render compatibility
    if (typeof window !== 'undefined') {
      return window.innerWidth >= theme.breakpoints.values.md;
    }
    return false; // Default to closed if window is not available
  };

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    sidebarOpen: getInitialSidebarState(),
  });

  // Single Redux store for all modules
  const moduleSlicesRef = useRef<Record<string, ModuleSlice>>({});
  const storeRef = useRef<Store | null>(null);

  const dataCache = useContext(DataCacheContext);

  // Track if this is the initial mount
  const isInitialMount = useRef(true);

  // Handle responsive sidebar state changes - only on initial mount or if user hasn't manually set preference
  useEffect(() => {
    // Skip on initial mount - we already set the state from sessionStorage
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Only auto-adjust if user hasn't manually toggled (no sessionStorage value means user hasn't interacted)
    const hasUserPreference = sessionStorage.getItem('sidebarOpen') !== null;
    if (!hasUserPreference) {
      dispatch({
        type: 'SET_SidebarOpen',
        payload: isDesktop,
      });
    }
  }, [isDesktop]);

  const setModules = useCallback((value: ModuleConfig[]) => {
    dispatch({ type: 'SET_Modules', payload: value });

    // Create Redux slices for each module and a single store
    const newSlices: Record<string, ModuleSlice> = {};
    const reducers: Record<
      string,
      ReturnType<typeof createModuleSlice>['reducer']
    > = {};

    value.forEach((module) => {
      const moduleSlice = createModuleSlice(module.name);
      newSlices[module.name] = moduleSlice;
      reducers[module.name] = moduleSlice.reducer;
    });

    // Create a single store with all module reducers
    if (Object.keys(reducers).length > 0) {
      const store = configureStore({
        reducer: reducers,
      });

      moduleSlicesRef.current = newSlices;
      storeRef.current = store;
    }
  }, []);

  const setModulesEnabled = useCallback((module: string, enabled: boolean) => {
    dispatch({ type: 'SET_Enabled', payload: { module, enabled } });
  }, []);

  const toggleLayerControl = useCallback(() => {
    dispatch({ type: 'TOGGLE_LayerControl' });
  }, []);

  const toggleHarControl = useCallback(() => {
    dispatch({ type: 'TOGGLE_HarControl' });
  }, []);

  const toggleSidebar = useCallback(() => {
    dispatch({ type: 'TOGGLE_Sidebar' });
  }, []);

  // Save sidebar state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('sidebarOpen', state.sidebarOpen.toString());
  }, [state.sidebarOpen]);

  // Direct store and slice access functions - these don't change when data updates
  const getStore = useCallback(() => storeRef.current, []);

  const getModuleSlice = useCallback((moduleName: string) => {
    return moduleSlicesRef.current[moduleName] || null;
  }, []);

  // Simple function to set data (for data cache)
  const setAllObjects = useCallback(
    (moduleName: string, objects: MapIcon[]) => {
      const store = storeRef.current;
      const moduleSlice = moduleSlicesRef.current[moduleName];

      if (store && moduleSlice) {
        store.dispatch(moduleSlice.actions.setAllObjects(objects));
      }
    },
    []
  );

  // Function to update individual objects (for real-time updates)
  const updateObject = useCallback(
    (moduleName: string, id: string, updates: Partial<MapIcon>) => {
      const store = storeRef.current;
      const moduleSlice = moduleSlicesRef.current[moduleName];

      if (store && moduleSlice) {
        store.dispatch(moduleSlice.actions.updateObject({ id, updates }));
        console.log(`Updated object ${id} in ${moduleName}:`, updates);
      }
    },
    []
  );

  // Set up data cache listeners
  useEffect(() => {
    if (!dataCache) return;

    state.modules.forEach((module) => {
      const endpoint = module.apiConfig?.endpoint;
      const updateDelay = module.apiConfig?.updateDelay ?? 0;
      const useFakeData = module.apiConfig?.useFakeData ?? false;

      if (endpoint) {
        // Set loading state to true when starting to fetch data
        const store = storeRef.current;
        const moduleSlice = moduleSlicesRef.current[module.name];
        if (store && moduleSlice) {
          store.dispatch(moduleSlice.actions.setLoading(true));
        }

        const setObjectsListener = (e: MessageEvent) => {
          setAllObjects(module.name, e.data);
        };

        dataCache.createDataCache(
          endpoint,
          updateDelay,
          setObjectsListener,
          useFakeData
        );
      }
    });
  }, [state.modules, dataCache, setAllObjects]);

  const contextValue = useMemo(
    () => ({
      modules: state.modules,
      setModules,
      setModulesEnabled,
      modulesEnabled: state.modulesEnabled,
      toggleLayerControl,
      toggleHarControl,
      toggleSidebar,
      layerControlOpen: state.layerControlOpen,
      harOpen: state.harOpen,
      sidebarOpen: state.sidebarOpen,
      getStore,
      getModuleSlice,
      updateObject,
    }),
    [
      state.modules,
      setModules,
      setModulesEnabled,
      state.modulesEnabled,
      toggleLayerControl,
      toggleHarControl,
      toggleSidebar,
      state.layerControlOpen,
      state.harOpen,
      state.sidebarOpen,
      getStore,
      getModuleSlice,
      updateObject,
    ]
  );

  return (
    <ModulesContext.Provider value={contextValue}>
      {children}
    </ModulesContext.Provider>
  );
};

export const useModules = (): ModulesContextValue => {
  const context = useContext(ModulesContext);
  if (!context) {
    throw new Error('useModules must be used within a ModulesProvider');
  }
  return context;
};
